import { useState } from "react";
import {
  Box,
  Button,
  Collapse,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Autocomplete,
  Chip,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import moment from "moment";
import { PresetFechas } from "../Informes";

export type IFiltrosInformes = {
  fechaInicio: Date | null;
  fechaFin: Date | null;
  tipo?: 'cita' | 'clase' | 'bono' | 'agrupada';
  estado?: 'Borrador' | 'Emitida' | 'Anulada';
  estadoPago?: 'Pendiente' | 'Pagada';
  servicios?: string[];
  empleado?: string;
  paciente?: string;
  metodoPago?: string;
};

interface FiltrosInformesProps {
  filtros: IFiltrosInformes;
  onChange: (partial: Partial<IFiltrosInformes>) => void;
  onReset?: () => void;
  opciones: {
    tipos: IFiltrosInformes['tipo'][];
    estados: IFiltrosInformes['estado'][];
    estadosPago: IFiltrosInformes['estadoPago'][];
    servicios: string[];
    empleados: IFiltrosInformes['empleado'][];
    pacientes: IFiltrosInformes['paciente'][];
    metodosPago: IFiltrosInformes['metodoPago'][];
  };
  presetFechas: PresetFechas;
  onPresetFechasChange: (preset: PresetFechas) => void;
}


const FiltrosInformes = ({ filtros, onChange, onReset, opciones, presetFechas, onPresetFechasChange }: FiltrosInformesProps) => {
  const [mostrarFiltrosAvanzados, setMostrarFiltrosAvanzados] = useState(false);

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      {/* FILTROS BÁSICOS */}
      <Box display="flex" gap={1} flexWrap="wrap" alignItems="center">
        <Box display="flex" gap={1} flexWrap="wrap">
          {["Hoy", "Este mes", "3 meses", "1 año", "Personalizado"].map(option => (
            <Chip
              key={option}
              label={option}
              color={presetFechas === option ? "primary" : "default"}
              onClick={() => onPresetFechasChange(option as PresetFechas)}
            />
          ))}
        </Box>

        {presetFechas === "Personalizado" && (
          <>
            <DatePicker
              label="Desde"
              value={filtros.fechaInicio ? moment(filtros.fechaInicio) : null}
              onChange={(date) => onChange({ fechaInicio: date ? date.toDate() : null })}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  sx={{ minWidth: { xs: "100%", sm: 150 }, "& .MuiInputLabel-root, & .MuiInputBase-input": { fontSize: 13 } }}
                />
              )}
            />
            <DatePicker
              label="Hasta"
              value={filtros.fechaFin ? moment(filtros.fechaFin) : null}
              minDate={filtros.fechaInicio ? moment(filtros.fechaInicio) : undefined}
              onChange={(date) => onChange({ fechaFin: date ? date.toDate() : null })}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  sx={{ minWidth: { xs: "100%", sm: 150 }, "& .MuiInputLabel-root, & .MuiInputBase-input": { fontSize: 13 } }}
                />
              )}
            />
          </>
        )}

        <Button
          variant="outlined"
          onClick={() => setMostrarFiltrosAvanzados(!mostrarFiltrosAvanzados)}
          sx={{ whiteSpace: "nowrap", width: { xs: "100%", sm: "auto" } }}
        >
          {mostrarFiltrosAvanzados ? "Ocultar filtros" : "Más filtros"}
        </Button>

        {mostrarFiltrosAvanzados && onReset && (
          <Button
            variant="outlined"
            onClick={onReset}
            sx={{ whiteSpace: "nowrap", ml: "auto", width: { xs: "100%", sm: "auto" } }}
          >
            Limpiar filtros
          </Button>
        )}
      </Box>

      {/* FILTROS AVANZADOS */}
      <Collapse in={mostrarFiltrosAvanzados}>
        <Box display="flex" flexWrap="wrap" gap={1}>
          <FormControl sx={{ width: { xs: "100%", sm: 150 } }} size="small">
            <InputLabel sx={{ fontSize: 13 }}>Tipo de factura</InputLabel>
            <Select
              value={filtros.tipo ?? ""}
              onChange={(e) => onChange({ tipo: e.target.value as IFiltrosInformes["tipo"] || undefined })}
              label="Tipo de factura"
              sx={{ "& .MuiInputLabel-root, & .MuiInputBase-input": { fontSize: 13 } }}
            >
              {opciones.tipos.map((t) => (
                <MenuItem key={t} value={t}>
                  {t!.charAt(0).toUpperCase() + t!.slice(1)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl sx={{ width: { xs: "100%", sm: 150 } }} size="small">
            <InputLabel sx={{ fontSize: 13 }}>Servicio</InputLabel>
            <Select
              multiple
              value={filtros.servicios ?? []}
              onChange={(e) => {
                const value = e.target.value;
                onChange({ servicios: typeof value === "string" ? value.split(",") : value });
              }}
              label="Servicio"
              renderValue={(selected) => (selected as string[]).join(", ")}
              sx={{ "& .MuiInputLabel-root, & .MuiInputBase-input": { fontSize: 13 } }}
            >
              {opciones.servicios.map((t) => (
                <MenuItem key={t} value={t}>
                  {t!.charAt(0).toUpperCase() + t!.slice(1)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl sx={{ width: { xs: "100%", sm: 165 } }} size="small">
            <InputLabel sx={{ fontSize: 13 }}>Método de pago</InputLabel>
            <Select
              value={filtros.metodoPago ?? ""}
              onChange={(e) => onChange({ metodoPago: e.target.value || undefined })}
              label="Método de pago"
              sx={{ "& .MuiInputLabel-root, & .MuiInputBase-input": { fontSize: 13 } }}
            >
              {opciones.metodosPago.map((m) => (
                <MenuItem key={m} value={m}>
                  {m!.charAt(0).toUpperCase() + m!.slice(1)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl sx={{ width: { xs: "100%", sm: 150 } }} size="small">
            <InputLabel sx={{ fontSize: 13 }}>Estado factura</InputLabel>
            <Select
              value={filtros.estado ?? ""}
              onChange={(e) => onChange({ estado: e.target.value as IFiltrosInformes["estado"] || undefined })}
              label="Estado factura"
              sx={{ "& .MuiInputLabel-root, & .MuiInputBase-input": { fontSize: 13 } }}
            >
              {opciones.estados.map((s) => (
                <MenuItem key={s} value={s}>
                  {s!.charAt(0).toUpperCase() + s!.slice(1)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl sx={{ width: { xs: "100%", sm: 150 } }} size="small">
            <InputLabel sx={{ fontSize: 13 }}>Estado pago</InputLabel>
            <Select
              value={filtros.estadoPago ?? ""}
              onChange={(e) => onChange({ estadoPago: e.target.value as IFiltrosInformes["estadoPago"] || undefined })}
              label="Estado pago"
              sx={{ "& .MuiInputLabel-root, & .MuiInputBase-input": { fontSize: 13 } }}
            >
              {opciones.estadosPago.map((s) => (
                <MenuItem key={s} value={s}>
                  {s!.charAt(0).toUpperCase() + s!.slice(1)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl sx={{ width: { xs: "100%", sm: 150 } }} size="small">
            <InputLabel sx={{ fontSize: 13 }}>Empleado</InputLabel>
            <Select
              value={filtros.empleado ?? ""}
              onChange={(e) => onChange({ empleado: e.target.value || undefined })}
              label="Empleado"
              sx={{ "& .MuiInputLabel-root, & .MuiInputBase-input": { fontSize: 13 } }}
            >
              {opciones.empleados.map((e) => (
                <MenuItem key={e} value={e}>{e}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <Autocomplete
            disablePortal
            options={opciones.pacientes}
            value={filtros.paciente ?? null}
            onChange={(e, value) => onChange({ paciente: value ?? undefined })}
            sx={{
              flexGrow: 1,
              minWidth: { xs: "100%", sm: 188, "& .MuiInputBase-root": { height: 40 }, },
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Paciente"
                size="small"
                sx={{ "& .MuiInputLabel-root, & .MuiInputBase-input": { fontSize: 13 } }}
              />
            )}
          />
        </Box>
      </Collapse>
    </LocalizationProvider>
  );
};

export default FiltrosInformes;
